import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Heart, Award, CreditCard, Bell,
  Calendar, ClipboardList, MapPin, Phone,
  Clock, CheckSquare, Users
} from 'lucide-react';

// Role-specific bottom nav tabs (max 5 per role)
const BOTTOM_TABS = {
  parent: [
    { path: '/parent', label: 'Home', icon: LayoutDashboard },
    { path: '/parent/tournaments', label: 'Tournaments', icon: Award },
    { path: '/parent/billing', label: 'Billing', icon: CreditCard },
    { path: '/parent/children', label: 'Family', icon: Heart },
    { path: '/parent/notifications', label: 'Alerts', icon: Bell, showBadge: true },
  ],
  coach: [
    { path: '/coach', label: 'Home', icon: LayoutDashboard },
    { path: '/coach/tournaments', label: 'Tournaments', icon: Award },
    { path: '/coach/attendance', label: 'Attendance', icon: CheckSquare },
    { path: '/coach/hours', label: 'Hours', icon: Clock },
    { path: '/coach/profile', label: 'Profile', icon: Users },
  ],
  staff: [
    { path: '/staff', label: 'Home', icon: LayoutDashboard },
    { path: '/staff/schedule', label: 'Schedule', icon: Calendar },
    { path: '/staff/hours', label: 'Hours', icon: Clock },
    { path: '/staff/enrollments', label: 'Enrollments', icon: ClipboardList },
    { path: '/staff/profile', label: 'Profile', icon: Users },
  ],
  customer: [
    { path: '/book', label: 'Book Lane', icon: MapPin },
    { path: '/customer/contact', label: 'Contact', icon: Phone },
  ],
  player: [
    { path: '/player', label: 'Home', icon: LayoutDashboard },
    { path: '/player/book-lane', label: 'Book Lane', icon: MapPin },
    { path: '/player/contact', label: 'Contact', icon: Phone },
  ],
  admin: [
    { path: '/admin', label: 'Home', icon: LayoutDashboard },
    { path: '/admin/tournaments', label: 'Tournaments', icon: Award },
    { path: '/admin/lanes', label: 'Lanes', icon: MapPin },
    { path: '/admin/enrollments', label: 'Enrollments', icon: ClipboardList },
    { path: '/admin/billing-management', label: 'Billing', icon: CreditCard },
  ],
};

export const BottomNav = ({ notificationCount = 0 }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (!user?.role) return null;

  const tabs = BOTTOM_TABS[user.role] || [];
  if (tabs.length === 0) return null;

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 safe-area-bottom"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="bottom-nav"
    >
      <div className="flex items-center justify-around h-16">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = location.pathname === tab.path ||
            (tab.path !== '/' && tab.path.length > 1 && location.pathname.startsWith(tab.path + '/'));
          const showBadge = tab.showBadge && notificationCount > 0;

          return (
            <Link
              key={tab.path}
              to={tab.path}
              className={`
                flex flex-col items-center justify-center flex-1 h-full relative
                transition-colors duration-150
                ${isActive
                  ? 'text-blue-600'
                  : 'text-slate-400 active:text-slate-600'
                }
              `}
              data-testid={`bottom-tab-${tab.label.toLowerCase().replace(' ', '-')}`}
            >
              {isActive && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-blue-600 rounded-b" />
              )}
              <div className="relative">
                <Icon className={`h-5 w-5 ${isActive ? 'stroke-[2.5]' : ''}`} />
                {showBadge && (
                  <span className="absolute -top-1.5 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </div>
              <span className={`text-[10px] mt-0.5 ${isActive ? 'font-semibold' : 'font-medium'}`}>
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
